/**
 * Recover from a stale chunk after a deploy.
 *
 * The dashboard code-splits across 151 lazy routes, so a tab that was
 * open when a deploy landed is holding an index that names chunk files
 * the server no longer has. Nothing breaks until the user navigates to a
 * route they hadn't visited yet — then the dynamic import 404s, Vite
 * fires `vite:preloadError`, and the route renders the ErrorBoundary
 * instead. `/analytics/assistant` is where Sentry caught it, but every
 * lazy route has the same exposure.
 *
 * The fix is to reload, because the index is what's stale. The danger is
 * that a chunk can 404 for reasons a reload will never fix (a broken
 * build, a bad CDN path), and reloading on those produces an infinite
 * reload loop — a far worse failure than the error screen, and one the
 * user cannot escape without closing the tab.
 *
 * So the reload is guarded, and the guard is the important half:
 *
 *   - At most one reload per tab per RETRY_WINDOW_MS.
 *   - The marker lives in sessionStorage, which survives the reload and
 *     dies with the tab.
 *   - If sessionStorage is unavailable (Safari private mode, blocked
 *     storage), we DO NOT reload at all. Without somewhere to record
 *     the attempt there is no way to stop a loop, and an error screen
 *     beats a tab that reloads forever.
 *
 * When the guard trips — meaning we already reloaded and the import
 * still failed — that is a real bug rather than a stale deploy, so it
 * goes to Sentry with the distinction made explicit.
 */
import * as Sentry from '@sentry/react';

const RELOAD_MARKER = 'cos:stale-chunk-reload-at';

/**
 * How recently a reload has to have happened for us to conclude that
 * reloading isn't working. Generous enough to cover a slow reload on a
 * bad connection; short enough that a genuine second deploy an hour
 * later still gets its own recovery.
 */
const RETRY_WINDOW_MS = 30_000;

/** Vite's event carries the original import error on `payload`. */
interface PreloadErrorEvent extends Event {
  payload?: unknown;
}

/** sessionStorage throws rather than returning null when blocked. */
function readMarker(): number | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARKER);
    if (!raw) return null;
    const at = Number(raw);
    return Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
}

/** Returns false when the marker could not be persisted. */
function writeMarker(now: number): boolean {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(now));
    return true;
  } catch {
    return false;
  }
}

export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const error = (event as PreloadErrorEvent).payload;
    const now = Date.now();
    const lastReload = readMarker();

    if (lastReload !== null && now - lastReload < RETRY_WINDOW_MS) {
      // We already tried. Reloading again would loop. Let the error
      // through so the ErrorBoundary renders and the user gets a
      // "Reload page" button they control.
      Sentry.captureException(error, {
        level: 'error',
        tags: { stale_chunk_reload: 'exhausted' },
        extra: {
          note:
            'Dynamic import failed again after an automatic reload, so ' +
            'this is not a stale deploy. Suspect a broken build or an ' +
            'unreachable asset path.',
          msSinceReload: now - lastReload,
        },
      });
      return;
    }

    if (!writeMarker(now)) {
      // No way to record the attempt, so no way to stop a loop.
      Sentry.captureException(error, {
        level: 'warning',
        tags: { stale_chunk_reload: 'unguarded' },
        extra: {
          note:
            'Dynamic import failed and sessionStorage is unavailable, so ' +
            'the automatic reload was skipped to avoid an unstoppable ' +
            'reload loop. The user sees the error screen instead.',
        },
      });
      return;
    }

    // Stop Vite rethrowing: we're handling it by reloading.
    event.preventDefault();

    Sentry.captureMessage('Reloading after stale chunk', {
      level: 'info',
      tags: { stale_chunk_reload: 'recovering' },
    });

    // `reload()` re-requests the document, picking up the new index.
    window.location.reload();
  });
}
